

import { supabase } from '@/integrations/supabase/client';

// Mock данные для графиков, если не удалось получить данные из Supabase
const mockRevenueData = [
  { month: 'Янв', revenue: 45000 },
  { month: 'Фев', revenue: 55000 },
  { month: 'Март', revenue: 68000 },
  { month: 'Апр', revenue: 72000 },
  { month: 'Май', revenue: 85000 },
  { month: 'Июнь', revenue: 92000 },
];

const mockBikeStatusData = [
  { status: 'Доступны', count: 120 },
  { status: 'В ремонте', count: 15 },
  { status: 'На обслуживании', count: 5 },
  { status: 'Забронированы', count: 10 },
];

// Добавим недостающие mock данные
const mockFleetMixData = [
  { name: 'Standard', value: 150, percentage: 60 },
  { name: 'Premium', value: 75, percentage: 30 },
  { name: 'Electric', value: 25, percentage: 10 }
];

// Тарифные данные
const mockTariffData = [
  { name: 'Еженедельный', price: 4500, period: 'неделя' },
  { name: 'Двухнедельный', price: 8000, period: '2 недели' },
  { name: 'Месячный', price: 14000, period: 'месяц' },
  { name: 'Аренда с выкупом', price: 20000, period: 'аванс' }
];

// Функция для получения данных о доходах по месяцам
export const getRevenueData = async (): Promise<any[]> => {
  console.log('Получение данных о доходах...');
  
  try {
    const { data, error } = await supabase
      .from('revenue_analytics')
      .select('month, revenue')
      .order('month', { ascending: true });
    
    if (error) {
      console.error('Ошибка при получении данных о доходах:', error);
      return mockRevenueData;
    }
    
    if (!data || !Array.isArray(data)) {
      console.warn('Нет данных о доходах, используем моковые данные');
      return mockRevenueData;
    }
    
    console.log('Данные о доходах получены:', data);
    return data;
  } catch (error) {
    console.error('Ошибка при получении данных о доходах:', error);
    return mockRevenueData;
  }
};

// Алиас для совместимости
export const fetchRevenueData = getRevenueData;

// Функция для получения тарифных данных
export const getTariffData = () => {
  return mockTariffData;
};

// Функция для получения данных о статусе велосипедов
export const getBikeStatusData = async (): Promise<any[]> => {
  console.log('Получение данных о статусе велосипедов...');
  
  try {
    const { data, error } = await supabase
      .from('bike_status')
      .select('status, count');
    
    if (error) {
      console.error('Ошибка при получении данных о статусе велосипедов:', error);
      return mockBikeStatusData;
    }
    
    if (!data || !Array.isArray(data)) {
      console.warn('Нет данных о статусе велосипедов, используем моковые данные');
      return mockBikeStatusData;
    }
    
    console.log('Данные о статусе велосипедов получены:', data);
    return data;
  } catch (error) {
    console.error('Ошибка при получении данных о статусе велосипедов:', error);
    return mockBikeStatusData;
  }
};

export const getBikeModelStats = async (): Promise<any[]> => {
  console.log('Получение статистики по моделям велосипедов...');
  
  try {
    const { data, error } = await supabase
      .from('bikes')
      .select(`
        id,
        serial_number,
        model
      `);

    if (error) {
      console.error('Ошибка при получении данных о велосипедах:', error);
      return mockFleetMixData;
    }

    if (!data || !Array.isArray(data)) {
      console.warn('Нет данных о велосипедах, используем моковые данные');
      return mockFleetMixData;
    }

    // Группируем велосипеды по моделям
    const modelStats = data.reduce((acc, bike) => {
      const model = bike?.model || 'Неизвестная модель';
      
      if (!acc[model]) {
        acc[model] = 0;
      }
      acc[model]++;
      return acc;
    }, {} as Record<string, number>);

    // Конвертируем в нужный формат
    const totalBikes = data.length;
    const result = Object.entries(modelStats).map(([model, count]) => ({
      name: model,
      value: count,
      percentage: Math.round((count / totalBikes) * 100)
    }));

    console.log('Статистика по моделям велосипедов получена:', result);
    return result.length > 0 ? result : mockFleetMixData;

  } catch (error) {
    console.error('Ошибка при получении статистики по моделям:', error);
    return mockFleetMixData;
  }
};

// Функция для получения ключевых метрик
export const fetchKeyMetrics = async () => {
  try {
    const [payments, rentals, couriers] = await Promise.all([
      supabase.from('payments').select('amount'),
      supabase.from('rentals').select('id').eq('status', 'active'),
      supabase.from('couriers').select('id').eq('status', 'active')
    ]);

    const totalRevenue = payments.data?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
    const activeRentals = rentals.data?.length || 0;
    const activeCouriers = couriers.data?.length || 0;

    return {
      totalRevenue,
      activeRentals,
      activeCouriers,
      avgRentalAmount: activeRentals > 0 ? totalRevenue / activeRentals : 0
    };
  } catch (error) {
    console.error('Ошибка при получении ключевых метрик:', error);
    return {
      totalRevenue: 0,
      activeRentals: 0,
      activeCouriers: 0,
      avgRentalAmount: 0
    };
  }
};

// Функция для получения статистики дашборда
export const fetchDashboardStats = async () => {
  try {
    console.log('Загрузка статистики дашборда...');
    
    // Получаем все платежи для расчета общей выручки - учитываем оба статуса
    const { data: allPayments, error: paymentsError } = await supabase
      .from('payments')
      .select('amount, payment_date')
      .in('status', ['paid', 'completed']);

    if (paymentsError) {
      console.error('Ошибка при получении платежей:', paymentsError);
    }

    // Получаем ВСЕ аренды (без фильтра по активному статусу) с платежами
    const { data: allRentals, error: rentalsError } = await supabase
      .from('rentals')
      .select(`
        id,
        courier_id,
        bike_id,
        status,
        payments:payments!rental_id (
          id,
          amount,
          status
        )
      `);

    if (rentalsError) {
      console.error('Ошибка при получении арендаторов:', rentalsError);
    }

    // Получаем ВСЕХ курьеров (без фильтра по активному статусу) с арендами и платежами
    const { data: allCouriers, error: couriersError } = await supabase
      .from('couriers')
      .select(`
        id,
        rentals:rentals!courier_id (
          id,
          status,
          payments:payments!rental_id (
            id,
            status
          )
        )
      `);

    if (couriersError) {
      console.error('Ошибка при получении курьеров:', couriersError);
    }

    // Получаем все велосипеды для правильного подсчета активных
    const { data: allBikes, error: bikesError } = await supabase
      .from('bikes')
      .select('id, status');

    if (bikesError) {
      console.error('Ошибка при получении велосипедов:', bikesError);
    }

    // Расчет метрик
    const totalPayments = allPayments?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;
    
    // Доход за текущий месяц
    const currentDate = new Date();
    const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const currentMonthRevenue = allPayments
      ?.filter(payment => new Date(payment.payment_date) >= currentMonthStart)
      ?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;

    // Доход за прошлый месяц для расчета роста
    const lastMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const lastMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
    const lastMonthRevenue = allPayments
      ?.filter(payment => {
        const paymentDate = new Date(payment.payment_date);
        return paymentDate >= lastMonthStart && paymentDate <= lastMonthEnd;
      })
      ?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;

    // Расчет прироста дохода
    const revenueGrowth = lastMonthRevenue > 0 
      ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 
      : 0;

    // Оплаченные аренды (ВСЕ аренды у которых есть платежи со статусом paid или completed)
    const paidRentals = allRentals?.filter(rental => {
      const payments = Array.isArray(rental.payments) ? rental.payments : [];
      return payments.some(payment => payment.status === 'paid' || payment.status === 'completed');
    }) || [];

    // Курьеры которые оплатили (ВСЕ курьеры у которых есть хоть один платеж со статусом paid или completed)
    const couriersWhoPaid = allCouriers?.filter(courier => {
      const rentals = Array.isArray(courier.rentals) ? courier.rentals : [];
      return rentals.some(rental => {
        const payments = Array.isArray(rental.payments) ? rental.payments : [];
        return payments.some(payment => payment.status === 'paid' || payment.status === 'completed');
      });
    }) || [];

    // Правильный расчет среднего чека - средняя выручка с одной аренды
    const avgRentalAmount = paidRentals.length > 0 
      ? paidRentals.reduce((sum, rental) => {
          const payments = Array.isArray(rental.payments) ? rental.payments : [];
          const rentalRevenue = payments
            .filter(payment => payment.status === 'paid' || payment.status === 'completed')
            .reduce((paymentSum, payment) => paymentSum + (payment.amount || 0), 0);
          return sum + rentalRevenue;
        }, 0) / paidRentals.length
      : 0;

    // Правильный расчет активных велосипедов (включая pending аренды)
    const totalBikes = allBikes?.length || 0;
    
    // Находим велосипеды в pending арендах
    const bikesInPendingRentals = allRentals
      ?.filter(rental => rental.status === 'pending' && rental.bike_id)
      ?.map(rental => rental.bike_id) || [];

    // Активные велосипеды = in_use + в pending арендах (без дублирования)
    const activeBikesSet = new Set([
      ...allBikes?.filter(bike => bike.status === 'in_use').map(bike => bike.id) || [],
      ...bikesInPendingRentals
    ]);
    const activeBikes = activeBikesSet.size;

    const stats = {
      totalPayments,
      currentMonthRevenue,
      activeRentals: paidRentals.length,
      activeCouriers: couriersWhoPaid.length,
      avgRentalAmount,
      totalBikes,
      activeBikes,
      revenueGrowth
    };

    console.log('Статистика дашборда загружена:', stats);
    return stats;

  } catch (error) {
    console.error('Ошибка при загрузке статистики дашборда:', error);
    return {
      totalPayments: 0,
      currentMonthRevenue: 0,
      activeRentals: 0,
      activeCouriers: 0,
      avgRentalAmount: 0,
      totalBikes: 0,
      activeBikes: 0,
      revenueGrowth: 0
    };
  }
};

// Функция для расчета среднего чека
export const calculateAverageReceipt = async (): Promise<number> => {
  try {
    const { data: payments, error } = await supabase
      .from('payments')
      .select('amount')
      .in('status', ['paid', 'completed']);

    if (error || !payments?.length) {
      return 0;
    }

    const total = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    return total / payments.length;
  } catch (error) {
    console.error('Ошибка при расчете среднего чека:', error);
    return 0;
  }
};

// Функция для получения данных о доходности велосипедов
export const fetchBikeRevenueData = async (startDate: Date, endDate: Date) => {
  try {
    console.log('Загрузка данных о доходности велосипедов...');
    
    const { data: bikes, error } = await supabase
      .from('bikes')
      .select(`
        id,
        serial_number,
        model,
        rentals:rentals!bike_id (
          id,
          amount,
          start_date,
          payments:payments!rental_id (
            amount,
            payment_date,
            status
          )
        )
      `);

    if (error) {
      console.error('Ошибка при получении данных велосипедов:', error);
      return [];
    }

    if (!bikes) return [];

    return bikes.map(bike => {
      const rentals = Array.isArray(bike.rentals) ? bike.rentals : [];
      const totalRevenue = rentals.reduce((sum, rental) => {
        const payments = Array.isArray(rental.payments) ? rental.payments : [];
        const rentalRevenue = payments
          .filter(payment => payment.status === 'paid' || payment.status === 'completed')
          .reduce((paymentSum, payment) => paymentSum + (payment.amount || 0), 0);
        return sum + rentalRevenue;
      }, 0);

      return {
        bikeId: bike.id,
        serialNumber: bike.serial_number || 'Неизвестно',
        model: bike.model || 'Неизвестная модель',
        totalRevenue,
        rentalCount: rentals.length,
        averageRevenue: rentals.length > 0 ? totalRevenue / rentals.length : 0,
        monthlyData: {} // Можно добавить детализацию по месяцам позже
      };
    });

  } catch (error) {
    console.error('Ошибка при загрузке данных о доходности велосипедов:', error);
    return [];
  }
};

export const getCourierPerformanceData = async (): Promise<any[]> => {
  console.log('Получение данных о производительности курьеров...');
  
  try {
    const { data, error } = await supabase
      .from('courier_performance')
      .select('*');
    
    if (error) {
      console.error('Ошибка при получении данных о производительности курьеров:', error);
      return [];
    }
    
    if (!data || !Array.isArray(data)) {
      console.warn('Нет данных о производительности курьеров');
      return [];
    }
    
    console.log('Данные о производительности курьеров получены:', data);
    return data;
  } catch (error) {
    console.error('Ошибка при получении данных о производительности курьеров:', error);
    return [];
  }
};

export const getTopCouriersData = async (): Promise<any[]> => {
  console.log('Получение данных о лучших курьерах...');
  
  try {
    const { data, error } = await supabase
      .from('top_couriers')
      .select('*');
    
    if (error) {
      console.error('Ошибка при получении данных о лучших курьерах:', error);
      return [];
    }
    
    if (!data || !Array.isArray(data)) {
      console.warn('Нет данных о лучших курьерах');
      return [];
    }
    
    console.log('Данные о лучших курьерах получены:', data);
    return data;
  } catch (error) {
    console.error('Ошибка при получении данных о лучших курьерах:', error);
    return [];
  }
};

export const getFleetMixData = async (): Promise<any[]> => {
  console.log('Получение данных о составе флота...');
  
  try {
    // Используем getBikeModelStats для получения данных о составе флота
    const modelStats = await getBikeModelStats();
    return modelStats.length > 0 ? modelStats : mockFleetMixData;
    
  } catch (error) {
    console.error('Ошибка при получении данных о составе флота:', error);
    return mockFleetMixData;
  }
};
